/**
 * Error boundary component for graceful error handling
 */

import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // In production, send to error reporting service
    if (import.meta.env.PROD) {
      // errorReportingService.captureException(error, {
      //   tags: { component: 'ErrorBoundary' },
      //   extra: errorInfo,
      // });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, retryCount } = this.state;
      const { fallback: CustomFallback } = this.props;

      // Use custom fallback if provided
      if (CustomFallback) {
        return (
          <CustomFallback 
            error={error}
            errorInfo={errorInfo}
            retry={this.handleRetry}
            goHome={this.handleGoHome}
          />
        );
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-xl shadow-medium p-8 text-center">
              {/* Error icon */}
              <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              
              {/* Error message */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Oops! Something went wrong
              </h2>
              
              <p className="text-gray-600 mb-6">
                We encountered an unexpected error. Don't worry, our team has been notified and we're working to fix it.
              </p>
              
              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="btn-primary flex items-center justify-center space-x-2"
                  disabled={retryCount >= 3}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>
                    {retryCount >= 3 ? 'Max retries reached' : 'Try Again'}
                  </span>
                </button>
                
                <button
                  onClick={this.handleGoHome}
                  className="btn-outline flex items-center justify-center space-x-2"
                >
                  <Home className="w-4 h-4" />
                  <span>Go Home</span>
                </button>
              </div>
              
              {/* Error details in development */}
              {import.meta.env.DEV && (
                <details className="mt-6 text-left">
                  <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                    Error Details (Development)
                  </summary>
                  <div className="mt-3 p-4 bg-red-50 rounded-lg">
                    <div className="text-sm">
                      <p className="font-semibold text-red-800 mb-2">Error:</p>
                      <pre className="text-red-700 whitespace-pre-wrap text-xs mb-4">
                        {error?.toString()}
                      </pre>
                      
                      {errorInfo?.componentStack && (
                        <>
                          <p className="font-semibold text-red-800 mb-2">Component Stack:</p>
                          <pre className="text-red-700 whitespace-pre-wrap text-xs">
                            {errorInfo.componentStack}
                          </pre>
                        </>
                      )}
                    </div>
                  </div>
                </details>
              )}
              
              {/* Retry count indicator */}
              {retryCount > 0 && (
                <p className="mt-4 text-xs text-gray-500">
                  Retry attempts: {retryCount}/3
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional error boundary hook for simpler usage
export const useErrorHandler = () => {
  return (error, errorInfo) => {
    console.error('Error caught by useErrorHandler:', error, errorInfo);
    
    // In production, send to error reporting service
    if (import.meta.env.PROD) {
      // errorReportingService.captureException(error, {
      //   extra: errorInfo,
      // });
    }
  };
};

// HOC for wrapping components with error boundary
export const withErrorBoundary = (Component, fallback) => {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

export default ErrorBoundary;
